/**
 * The console's collaborative document-type catalog.
 *
 * The desktop equivalent is `CollaborativeDocumentTypeCatalog`: a live
 * projection over built-ins plus whatever extension bundles contribute. This is
 * the browser's much smaller version of the same thing, and it exists for the
 * same reason — `documentTypes()` used to be a frozen one-element array, so
 * every shared document that was not markdown resolved to "no host document
 * type can open this".
 *
 * Two rules it must not break:
 *
 *   - **Markdown is unchanged.** Its descriptor is what the frozen array held
 *     (plus page history, which it gained later), and it stays first.
 *   - **A type this console genuinely cannot open still lands on `unsupported`.**
 *     A descriptor with a `capabilities.disabledReason` is exactly how
 *     `resolveSharedDocumentTypePresentation` reports that, and it is a better
 *     answer than a missing descriptor because it can say *why*.
 *
 * A pin whose bundle has not been imported yet is reported as openable on the
 * strength of its manifest. That is the declaration a build-time pin makes, and
 * the alternative — downloading every pinned editor before the catalog can
 * answer — is what the lazy bundle exists to avoid. If the bundle then fails to
 * load, this catalog says so and the document surface offers the desktop app.
 */

import type { createCollabDocsScopeLifecycle } from '@nimbalyst/collab-bundle/docs-ui';
import {
  getBrowserExtensionsRevision,
  listBrowserExtensions,
  onBrowserExtensionsChanged,
  type BrowserCollabCodec,
  type BrowserExtensionEditorContribution,
  type BrowserExtensionEntry,
  type BrowserExtensionManifest,
} from '@nimbalyst/web-editors/registry';

type DocsHost = Parameters<typeof createCollabDocsScopeLifecycle>[0];
export type WebDocumentTypeDescriptor = ReturnType<
  NonNullable<DocsHost['documents']>['documentTypes']
>[number];

/**
 * Unchanged from the frozen array this catalog replaced. Markdown collab is
 * shipping production code; nothing here may alter what it resolves to.
 */
const MARKDOWN_DESCRIPTOR: WebDocumentTypeDescriptor = Object.freeze({
  documentType: 'markdown',
  displayName: 'Markdown',
  fileExtensions: ['.md', '.markdown'],
  defaultExtension: '.md',
  icon: 'description',
  editor: { kind: 'lexical' as const },
  content: { strategy: 'lexical' as const, codecId: 'markdown' },
  creation: { defaultContent: '', source: 'builtin' as const },
  capabilities: {
    localCreate: true,
    shareToTeam: false,
    sharedCreate: true,
    // Page history: `WebDocumentHistoryDialog`, the same dialog as the desktop.
    history: true,
    export: false,
    embed: false,
  },
});

function normalizeSuffix(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed.includes('/') || trimmed.includes('\\')) return null;
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

/** `*.csv` -> `.csv`. Anything richer than a suffix glob is not honoured. */
function suffixFromPattern(pattern: string): string | null {
  return pattern.startsWith('*.') ? normalizeSuffix(pattern.slice(1)) : null;
}

/** Longest first, so `.mockup.html` is preferred over `.html` when both match. */
function sortSuffixes(values: Iterable<string>): string[] {
  const normalized = Array.from(values, (value) => normalizeSuffix(value))
    .filter((value): value is string => value !== null);
  return [...new Set(normalized)].sort((a, b) => b.length - a.length || a.localeCompare(b));
}

function menuIcon(
  manifest: BrowserExtensionManifest,
  menuIconName: string | undefined,
  suffix: string,
): string {
  if (menuIconName) return menuIconName;
  for (const [pattern, icon] of Object.entries(manifest.contributions?.fileIcons ?? {})) {
    if (suffixFromPattern(pattern) === suffix) return icon;
  }
  return manifest.marketplace?.icon ?? 'extension';
}

function codecForSuffixes(
  codecs: readonly BrowserCollabCodec[],
  suffixes: readonly string[],
): BrowserCollabCodec | undefined {
  return codecs.find((codec) => codec.fileExtensions.some(
    (extension) => {
      const normalized = normalizeSuffix(extension);
      return normalized !== null && suffixes.includes(normalized);
    },
  ));
}

/**
 * Why this console cannot open a type, or `undefined` when it can.
 *
 * Everything a browser host can get wrong is here rather than spread across the
 * mount path, because this is the answer the tree, the list and the document
 * route all read — and a document reported openable that then renders nothing
 * is the failure this whole seam exists to prevent.
 */
function disabledReason(
  entry: BrowserExtensionEntry,
  contribution: BrowserExtensionEditorContribution,
): string | undefined {
  const label = entry.manifest.name || entry.manifest.id;
  if (contribution.collaboration?.supported !== true) {
    return contribution.collaboration?.unsupportedReason
      ?? `The "${label}" editor does not support collaborative editing.`;
  }
  // A declared-but-unloaded pin is taken at its manifest's word; the check
  // below can only be answered once the bundle has actually evaluated.
  if (entry.state.status !== 'loaded') return undefined;
  if (!entry.state.loaded.components[contribution.component]) {
    return `The "${label}" extension does not provide editor component "${contribution.component}".`;
  }
  return undefined;
}

/*
 * Two things this deliberately does NOT refuse over.
 *
 * A missing codec. The desktop catalog requires one because a desktop host
 * seeds a shared document from a file and exports it back to one. A browser
 * host has no file at either end: the extension hands its own `isEmpty` and
 * seed to `useCollaborativeEditor`, and the server persists the Y.Doc. Refusing
 * here would import a requirement that does not apply and would close a type
 * this console can genuinely open.
 *
 * A bundle that failed to load. That is a property of one fetch, not of the
 * document type — a chunk that failed once would otherwise relabel every
 * spreadsheet in the tree as unsupported for the rest of the session. The
 * failure belongs to the mount that attempted it, which reports it per open and
 * lets the next open try again.
 */

function descriptorsForExtension(entry: BrowserExtensionEntry): WebDocumentTypeDescriptor[] {
  const { manifest } = entry;
  const codecs = entry.state.status === 'loaded' ? entry.state.loaded.codecs : [];
  const menus = manifest.contributions?.newFileMenu ?? [];

  return (manifest.contributions?.customEditors ?? []).flatMap((contribution) => {
    const suffixes = sortSuffixes(
      contribution.filePatterns
        .map(suffixFromPattern)
        .filter((suffix): suffix is string => suffix !== null),
    );
    const primarySuffix = suffixes[0];
    if (!primarySuffix) return [];

    const codec = codecForSuffixes(codecs, suffixes);
    // Before the bundle loads there is no codec to name the document type, so
    // it comes from the manifest, and only then off the suffix. The suffix is
    // right for every type whose codec is named after its extension (`.csv` ->
    // `csv`), and wrong the moment one is not: `.prisma` -> `datamodel`. An
    // extension whose codec disagrees with its suffix declares the answer on
    // the collaboration block, because a shared document arrives labelled with
    // the codec's name and would otherwise match nothing until the bundle
    // happened to be loaded.
    const documentType = codec?.documentType
      ?? contribution.collaboration?.documentType
      ?? primarySuffix.slice(1);
    const menu = menus.find((candidate) => {
      const suffix = normalizeSuffix(candidate.extension);
      return candidate.action !== 'openVirtualTab' && suffix !== null && suffixes.includes(suffix);
    });
    const defaultExtension = (menu ? normalizeSuffix(menu.extension) : null) ?? primarySuffix;
    const reason = disabledReason(entry, contribution);

    return [{
      documentType,
      displayName: menu?.displayName ?? contribution.displayName,
      fileExtensions: sortSuffixes([...suffixes, ...(codec?.fileExtensions ?? [])]),
      defaultExtension,
      icon: menuIcon(manifest, menu?.icon, defaultExtension),
      editor: {
        kind: 'extension' as const,
        extensionId: manifest.id,
        componentName: contribution.component,
      },
      // The console never branches on `strategy`; the extension's codec is what
      // owns the Y.Doc layout, and `codecId` is the field that names it.
      content: { strategy: 'structured-yjs' as const, codecId: documentType },
      capabilities: {
        localCreate: false,
        shareToTeam: false,
        // Creating a shared document of an extension type from the browser is
        // not wired yet: nothing seeds the new room from the manifest's default
        // content, so offering it would create documents that open empty.
        sharedCreate: false,
        history: false,
        export: false,
        ...(reason ? { disabledReason: reason } : {}),
      },
    }];
  });
}

/** Markdown plus everything the registered extension pins contribute. */
export function buildWebDocumentTypeDescriptors(
  extensions: readonly BrowserExtensionEntry[] = listBrowserExtensions(),
): WebDocumentTypeDescriptor[] {
  return [MARKDOWN_DESCRIPTOR, ...extensions.flatMap(descriptorsForExtension)];
}

let cachedRevision = -1;
let cachedDescriptors: WebDocumentTypeDescriptor[] = [];

/**
 * The live catalog.
 *
 * Rebuilt only when the registry changes, because `documentTypes()` is called
 * during render by the tree, the list and the home view: a fresh array on every
 * call would keep their `useSyncExternalStore` snapshots unstable.
 */
export function webDocumentTypeDescriptors(): readonly WebDocumentTypeDescriptor[] {
  const revision = getBrowserExtensionsRevision();
  if (revision !== cachedRevision) {
    cachedRevision = revision;
    cachedDescriptors = buildWebDocumentTypeDescriptors();
  }
  return cachedDescriptors;
}

/**
 * The suffixes whose link, alone in its paragraph, becomes an embed in a page:
 * every extension editor this console can open, as the desktop embeds every
 * custom editor. Code files are left as links, and markdown is a page link.
 */
export function webEmbeddableSuffixes(): string[] {
  return webDocumentTypeDescriptors()
    .filter((descriptor) => descriptor.editor.kind === 'extension'
      && descriptor.documentType !== 'code'
      && !descriptor.capabilities.disabledReason)
    .flatMap((descriptor) => descriptor.fileExtensions);
}

export function onWebDocumentTypesChanged(listener: () => void): () => void {
  return onBrowserExtensionsChanged(listener);
}
